import GuideLayout, {
  H2,
  H3,
  P,
  UL,
  OL,
  Callout,
  ExternalLink,
  Strong,
  Em,
} from "../ui/GuideLayout.jsx";
import { Link } from "react-router-dom";

export default function HakkenVsSeishain() {
  return (
    <GuideLayout
      title="Hakken vs seishain: qual contrato escolher no Japão"
      subtitle="A diferença entre empreiteira e funcionário efetivo, com números reais de salário, benefícios e estabilidade. Qual vale mais a pena na sua fase de vida."
      slug="hakken-vs-seishain"
      description="Hakken (empreiteira) vs seishain (efetivo) no Japão. Comparativo de salário, benefícios, estabilidade, e qual escolher para sua situação."
      updated="Maio de 2026"
      readTime="9 min"
    >
      <P>
        Quase todo brasileiro chega no Japão como <Em>hakken</Em> (派遣 —
        funcionário de empreiteira). Depois de alguns anos, surge a
        pergunta: <Em>vale a pena virar seishain?</Em> A resposta depende
        muito de em que fase da vida você está. Este guia compara os dois
        em detalhe.
      </P>

      <H2 id="o-que-eh">O que cada um significa</H2>

      <H3>Hakken (派遣)</H3>
      <P>
        Você é funcionário formal da <Strong>empreiteira</Strong> (派遣会社 /
        hakken gaisha), não da fábrica. A empreiteira te &ldquo;aluga&rdquo;
        para a fábrica por um contrato curto. Quase todo brasileiro
        recém-chegado começa aqui.
      </P>
      <P>
        Características típicas:
      </P>
      <UL>
        <li>Pagamento por hora</li>
        <li>Contrato de 3-6 meses, renovável</li>
        <li>Sem bônus de verão e inverno na maioria dos casos</li>
        <li>Empreiteira frequentemente fornece apartamento (e desconta)</li>
        <li>Demissão fácil — &ldquo;não vai chamar mais essa semana&rdquo;</li>
        <li>Pode pular de fábrica em fábrica buscando melhor pagamento</li>
      </UL>

      <H3>Seishain (正社員)</H3>
      <P>
        Você é funcionário <Strong>efetivo direto da fábrica</Strong>.
        Equivalente ao CLT brasileiro. Salário mensal fixo, bônus, plano de
        saúde da empresa, aposentadoria completa, estabilidade legal.
      </P>
      <P>
        Características típicas:
      </P>
      <UL>
        <li>Salário mensal fixo (não por hora)</li>
        <li>Contrato sem prazo de término</li>
        <li>Bônus de verão (夏のボーナス) e inverno (冬のボーナス) — geralmente 1-3 meses cada</li>
        <li>Demissão muito difícil legalmente</li>
        <li>Plano de carreira, aumentos anuais</li>
        <li>Treinamento, internas, possibilidade de virar líder de turno</li>
        <li>Você procura o próprio apato</li>
      </UL>

      <H2 id="comparacao-financeira">Comparação financeira realista</H2>
      <P>
        Vamos comparar duas situações reais para um operário de 30 anos
        trabalhando na mesma fábrica (Toyota peças, Aichi):
      </P>

      <H3>Como hakken</H3>
      <UL>
        <li>Hora: ¥1.400</li>
        <li>176 horas/mês + 40 horas extras (zangyo)</li>
        <li>Bruto mensal: ¥246.400 + ¥70.000 (extras a ¥1.750) = <Strong>¥316.400</Strong></li>
        <li>Bônus: ¥0</li>
        <li><Strong>Total anual bruto: ¥3.796.800</Strong></li>
        <li>Líquido após descontos: ~¥3.000.000</li>
      </UL>

      <H3>Como seishain</H3>
      <UL>
        <li>Mensal fixo: ¥260.000</li>
        <li>Hora extra: pago à parte, mas comum ter limite</li>
        <li>Bônus de verão: 1,5 mês = ¥390.000</li>
        <li>Bônus de inverno: 2 meses = ¥520.000</li>
        <li>Outros benefícios (vale-transporte, almoço subsidiado): ~¥30.000 mensais valor equivalente</li>
        <li><Strong>Total anual bruto: ¥4.030.000</Strong></li>
        <li>Líquido após descontos: ~¥3.150.000</li>
      </UL>
      <P>
        <Strong>Diferença líquida anual:</Strong> ~¥150.000 a favor do
        seishain. Não é nada gigantesco. Por que tanta gente quer virar seishain?
        Porque o número anual esconde diferenças mais importantes.
      </P>

      <H2 id="alem-do-salario">O que o número não mostra</H2>

      <H3>Estabilidade</H3>
      <P>
        Hakken: a empreiteira pode te dispensar com 30 dias de aviso (ou
        sem aviso em alguns contratos), especialmente em recessão. Você
        viveu isso em 2008 (crise Lehman) e 2020 (covid) — milhares de
        brasileiros voltaram pro Brasil porque a fábrica &ldquo;não chamou
        mais&rdquo;.
      </P>
      <P>
        Seishain: lei japonesa torna demissão de seishain quase impossível.
        Em recessão, a empresa primeiro corta hakken (todos eles), depois
        reduz horas, depois oferece &ldquo;aposentadoria voluntária&rdquo;
        com bônus. Demissão real, só em casos extremos.
      </P>

      <H3>Renovação de visto</H3>
      <P>
        Imigração olha estabilidade ao renovar visto. Hakken com contrato
        de 3 meses tem chance de renovação aprovada mais curta (1-2 anos
        em vez de 3-5). Seishain com contrato sem prazo geralmente recebe
        máximo possível (5 anos para várias categorias).
      </P>

      <H3>Aposentadoria</H3>
      <P>
        Hakken paga apenas o básico do shakai hoken/kosei nenkin. Seishain
        em empresa grande frequentemente tem <Em>pension fund corporativo</Em>{" "}
        (企業年金) adicional, complementar à aposentadoria pública. Pode
        ser ¥50.000–150.000 a mais de aposentadoria mensal quando você se
        aposentar.
      </P>

      <H3>Plano de saúde</H3>
      <P>
        Shakai hoken cobre o básico em ambos. Mas empresas grandes (Toyota,
        Honda, Panasonic) frequentemente têm <Em>plano corporativo
        complementar</Em> que cobre o que o shakai hoken não cobre
        (médico particular, exames preventivos, fisioterapia). Cobre família também.
      </P>

      <H3>Bônus</H3>
      <P>
        O grande diferencial real. Em ano bom, empresa grande pode pagar 5
        meses de bônus (verão + inverno). Numa fábrica que paga ¥260.000/mês,
        isso é ¥1.300.000 extras por ano. Em ano ruim cai pra 2-3 meses,
        ainda é dinheiro real.
      </P>

      <H3>Aumento salarial</H3>
      <P>
        Hakken: aumento típico ¥10–30 por hora por ano de experiência na
        mesma fábrica. Em 5 anos, talvez ¥1.500 → ¥1.700.
      </P>
      <P>
        Seishain: aumento anual de 2-5% do salário base. Em 5 anos, sair de
        ¥260.000 → ¥320.000. E o bônus aumenta proporcionalmente.
      </P>

      <H3>Possibilidade de promoção</H3>
      <P>
        Hakken: praticamente zero. Você sempre será operário, mesmo depois
        de 10 anos.
      </P>
      <P>
        Seishain: progressão real é difícil pra brasileiro (idioma, cultura),
        mas existe. <Em>Han-chou</Em> (班長 — líder de turno) é alcançável,
        com ¥30.000–80.000 a mais por mês. Cargos acima exigem alto nível de
        japonês.
      </P>

      <H2 id="prioridades-fase">Como decidir baseado na sua fase de vida</H2>

      <H3>Recém-chegado, sem família, sem certeza de ficar</H3>
      <P>
        <Strong>Fique hakken.</Strong> A flexibilidade vale mais que a
        estabilidade. Você pode:
      </P>
      <UL>
        <li>Pular pra fábrica que paga ¥100 a mais por hora</li>
        <li>Pegar contrato com mais horas extras numa região cara</li>
        <li>Sair quando quiser sem multa séria</li>
        <li>Voltar pro Brasil sem complicação contratual</li>
      </UL>

      <H3>Família já no Japão, planeja ficar 3-5 anos mais</H3>
      <P>
        <Strong>Considere fortemente virar seishain.</Strong> A estabilidade
        protege a família, ajuda renovação de visto, e os bônus permitem
        guardar muito mais rapidamente.
      </P>

      <H3>Permanente ou caminho pra permanente</H3>
      <P>
        <Strong>Vire seishain.</Strong> Permanência é cumulativo — anos
        contínuos no mesmo emprego pesam favoravelmente. Bônus consistentes
        mostram renda estável. E imigração vê visa-renovações de seishain
        sem stress.
      </P>

      <H3>Idade 45+, pensando em aposentar no Japão</H3>
      <P>
        <Strong>Seishain é praticamente obrigatório.</Strong> O pension fund
        corporativo + os bônus acumulados nos últimos 20 anos de carreira
        fazem diferença gigante na aposentadoria. Hakken aos 65 vai depender
        só do kosei nenkin básico — bem menos confortável.
      </P>

      <H2 id="como-virar">Como virar seishain</H2>
      <P>
        Não é simples. Mas é mais possível do que muito brasileiro pensa.
      </P>

      <H3>Caminho 1: prova interna na empreiteira</H3>
      <P>
        Algumas fábricas (Toyota é o exemplo clássico) fazem <Em>seishain
        toyo shaiken</Em> (正社員登用試験) — prova para hakken passar a
        seishain. Você precisa de:
      </P>
      <UL>
        <li>2-3+ anos como hakken na mesma fábrica sem problema</li>
        <li>Japonês básico-intermediário (N3+ ajuda)</li>
        <li>Prova escrita: matemática, kanji, conhecimentos gerais, valores da empresa</li>
        <li>Prova prática: 5S, QCD, segurança do trabalho</li>
        <li>Entrevista</li>
      </UL>
      <P>
        Aprovação fica em 20-40% típico. Aprovado → contrato direto com a
        fábrica como seishain.
      </P>

      <H3>Caminho 2: aplicação externa</H3>
      <P>
        Procurar vagas seishain diretamente nos sites de emprego (HelloWork,
        Indeed Japão). Geralmente exigem:
      </P>
      <UL>
        <li>Japonês N3+ comprovado</li>
        <li>Experiência prévia comprovada (gensen choshuhyo de empregos anteriores)</li>
        <li>Currículo em japonês (rirekisho)</li>
        <li>Entrevista em japonês</li>
      </UL>
      <P>
        Difícil mas factível. Algumas fábricas menores são mais abertas a
        brasileiro do que as grandes.
      </P>

      <H3>Caminho 3: agência de recrutamento técnico</H3>
      <P>
        Existem agências (não empreiteiras tradicionais) que conectam
        estrangeiros com empresas que aceitam brasileiros como seishain.
        Exemplo: Indeed Japan, BizReach, Doda. Você cria perfil em japonês e
        empresas te contatam.
      </P>

      <H2 id="armadilha">A armadilha do &ldquo;quase seishain&rdquo;</H2>
      <P>
        Cuidado com algumas categorias que não são nem hakken nem seishain
        de verdade:
      </P>
      <UL>
        <li>
          <Strong>Keiyaku shain (契約社員):</Strong> contrato direto com a
          empresa mas com prazo determinado (1 ano, renovável). Não tem todos
          os direitos do seishain, mas é mais estável que hakken. Em algumas
          empresas é passo intermediário.
        </li>
        <li>
          <Strong>Junshain (準社員):</Strong> semi-funcionário, com benefícios
          parciais.
        </li>
        <li>
          <Strong>Patos (パート / arubaito):</Strong> meio-período, sem
          benefícios completos.
        </li>
      </UL>
      <P>
        Quando alguém te oferece &ldquo;contrato direto&rdquo;, pergunte
        exatamente qual categoria. Não é seishain só porque é direto.
      </P>

      <H2 id="conclusao">Conclusão</H2>
      <P>
        Hakken e seishain são duas vidas diferentes no mesmo país. Não
        existe certo ou errado — existe o que cabe na sua fase.
      </P>
      <P>
        Como regra geral:
      </P>
      <UL>
        <li><Strong>1-3 anos no Japão:</Strong> hakken é melhor — flexibilidade vale</li>
        <li><Strong>3-7 anos, família:</Strong> tente virar seishain — estabilidade vale</li>
        <li><Strong>7+ anos, permanente:</Strong> seishain é praticamente obrigatório pra aposentar bem</li>
      </UL>
      <P>
        Para calcular o impacto financeiro real do seu caso, use a{" "}
        <Link to="/calculadoras/salario" className="text-accent underline">
          calculadora de salário
        </Link>{" "}
        comparando os dois cenários (com e sem bônus).
      </P>
    </GuideLayout>
  );
}
